import { useState, useRef, useEffect, useMemo } from 'react';
import { supabase } from '../lib/supabase.js';
import { useAppContext } from './AppContext';
import { receiptsForItem, receiptFileEntries, fmtMoney } from '../utils';

const DOC_TYPES = ['Spec Sheet', 'Quote', 'Purchase Order', 'Proposal', 'Drawing', 'Other'];

const RECEIPT_KIND_LABEL = {
  vendor_deposit:      'deposit',
  vendor_final:        'final',
  vendor_full_payment: 'full payment',
  vendor_fee:          'fee',
  project_reimbursable:'reimbursable',
  personal:            'office',
};

function SpecSheetIcon() {
  return (
    <svg viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="36">
      <rect x="2" y="2" width="36" height="44" rx="3" fill="#e8f0fe" stroke="#1a56db" strokeWidth="1.5"/>
      <rect x="7" y="10" width="26" height="2.5" rx="1" fill="#1a56db"/>
      <rect x="7" y="16" width="20" height="2" rx="1" fill="#90b4f8"/>
      <rect x="7" y="21" width="24" height="2" rx="1" fill="#90b4f8"/>
      <rect x="7" y="26" width="18" height="2" rx="1" fill="#90b4f8"/>
      <rect x="7" y="31" width="22" height="2" rx="1" fill="#90b4f8"/>
      <rect x="7" y="36" width="14" height="2" rx="1" fill="#90b4f8"/>
    </svg>
  );
}

const DOC_CONFIGS = {
  'Quote':          { bg: '#fff8e1', border: '#f9a825', line: '#f9a825', label: 'Q' },
  'Purchase Order': { bg: '#e8f5e9', border: '#2e7d32', line: '#2e7d32', label: 'PO' },
  'Proposal':       { bg: '#fce4ec', border: '#c2185b', line: '#c2185b', label: 'P' },
  'Drawing':        { bg: '#f3e5f5', border: '#7b1fa2', line: '#7b1fa2', label: '✏' },
  'Receipt':        { bg: '#fdf2f8', border: '#D4007A', line: '#D4007A', label: 'R' },
  'Other':          { bg: '#f5f5f5', border: '#bdbdbd', line: '#757575', label: '•' },
};

function DocIcon({ type }) {
  const c = DOC_CONFIGS[type] || DOC_CONFIGS['Other'];
  return (
    <svg viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="36">
      <rect x="2" y="2" width="36" height="44" rx="3" fill={c.bg} stroke={c.border} strokeWidth="1.5"/>
      <rect x="7" y="10" width="26" height="2" rx="1" fill={c.line} opacity="0.4"/>
      <rect x="7" y="16" width="20" height="2" rx="1" fill={c.line} opacity="0.3"/>
      <rect x="7" y="21" width="24" height="2" rx="1" fill={c.line} opacity="0.3"/>
      <rect x="7" y="26" width="16" height="2" rx="1" fill={c.line} opacity="0.3"/>
      <text x="20" y="43" fontSize="8" fontWeight="800" fill={c.line} textAnchor="middle" fontFamily="system-ui,sans-serif">{c.label}</text>
    </svg>
  );
}

function PDFIcon({ type }) {
  // Color-coded PDF — uses doc type color but with PDF label
  const c = DOC_CONFIGS[type] || DOC_CONFIGS['Other'];
  return (
    <svg viewBox="0 0 40 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="30" height="36">
      <rect x="2" y="2" width="36" height="44" rx="3" fill={c.bg} stroke={c.border} strokeWidth="1.5"/>
      <rect x="7" y="10" width="26" height="2" rx="1" fill={c.line} opacity="0.4"/>
      <rect x="7" y="16" width="20" height="2" rx="1" fill={c.line} opacity="0.3"/>
      <text x="20" y="36" fontSize="9" fontWeight="800" fill={c.line} textAnchor="middle" fontFamily="system-ui,sans-serif">PDF</text>
    </svg>
  );
}


function isPDFFile(doc) {
  const mime = doc.mime_type || '';
  const name = doc.name || doc.file_name || '';
  return mime.includes('pdf') || name.toLowerCase().endsWith('.pdf');
}

export default function ItemDocuments({ itemId, compact, readOnly, images = [], heroIndex = 0, onImageClick }) {
  // Receipts are DERIVED from AppContext, never uploaded here — the
  // receipt file exists once (Receipts tab) and this panel is a window
  // onto it (Cimon 2026-07-12). Tolerate a missing/mocked provider so
  // existing test harnesses that render without AppProvider keep working.
  let appCtx = null;
  try { appCtx = useAppContext(); } catch (e) { appCtx = null; }
  const linkedReceiptFiles = useMemo(() => {
    if (!appCtx || !itemId) return [];
    const linked = receiptsForItem(itemId, appCtx) || [];
    const out = [];
    linked.forEach(rc => {
      (receiptFileEntries(rc, appCtx.receiptAttachments) || []).forEach(f => {
        out.push({ receipt: rc, file: f });
      });
    });
    return out;
  }, [appCtx && appCtx.receipts, appCtx && appCtx.receiptAttachments,
      appCtx && appCtx.vendorPayments, appCtx && appCtx.vendorPaymentItems,
      appCtx && appCtx.purchaseOrders, itemId]);

  async function openReceiptPreview(entry) {
    const { data, error: signErr } = await supabase.storage
      .from('receipts')
      .createSignedUrl(entry.file.storagePath, 3600);
    if (data?.signedUrl) {
      setPreviewUrl(data.signedUrl);
      setPreviewDoc({
        label: receiptTileTitle(entry),
        name: entry.file.filename,
        file_name: entry.file.filename,
        mime_type: entry.file.mimeType,
        doc_type: 'Receipt',
      });
      return;
    }
    console.warn('openReceiptPreview signed URL error:', signErr);
    setError('Could not open the receipt file.');
  }

  function receiptTileTitle(entry) {
    const rc = entry.receipt;
    const bits = [rc.vendorName || 'Receipt', RECEIPT_KIND_LABEL[rc.kind] || rc.kind];
    if (entry.file.role && entry.file.role !== 'invoice') bits.push(entry.file.role);
    if (rc.totalAmount) bits.push(fmtMoney(rc.totalAmount));
    if (rc.receiptDate) bits.push(rc.receiptDate);
    return bits.filter(Boolean).join(' · ');
  }

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDocType, setEditDocType] = useState('');
  const [uploadDocType, setUploadDocType] = useState('Spec Sheet');
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => { if (itemId) loadDocs(); }, [itemId]);

  useEffect(() => {
    function onPaste(e) {
      if (!itemId || readOnly) return;
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) handleUpload(file);
        }
      }
    }
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, [itemId, uploadDocType, readOnly]);

  async function loadDocs() {
    if (!itemId) return;
    setLoading(true);
    const { data, error: loadErr } = await supabase
      .from('item_documents')
      .select('*')
      .eq('item_id', itemId)
      .order('created_at', { ascending: false });
    if (loadErr) console.warn('loadDocs error:', loadErr);
    setDocs(data || []);
    setLoading(false);
  }

  async function handleUpload(file) {
    if (!file || !itemId) return;
    setUploading(true); setError('');

    let uploadFile = file;
    if (file.name.toLowerCase().endsWith('.heic') || file.type === 'image/heic') {
      try {
        const { default: heic2any } = await import('https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js');
        const blob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.85 });
        uploadFile = new File([blob], file.name.replace(/\.heic$/i, '.jpg'), { type: 'image/jpeg' });
      } catch { /* proceed */ }
    }

    const safeName = uploadFile.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `item-docs/${itemId}/${Date.now()}-${safeName}`;

    const { error: upErr } = await supabase.storage
      .from('item-documents')
      .upload(path, uploadFile, { upsert: true });

    if (upErr) { setError('Upload failed: ' + upErr.message); setUploading(false); return; }

    // Get the public URL
    const { data: urlData } = supabase.storage.from('item-documents').getPublicUrl(path);
    const url = urlData?.publicUrl || '';

    const label = uploadFile.name.replace(/\.[^.]+$/, '');
    const docType = uploadDocType;

    const { error: dbErr } = await supabase.from('item_documents').insert({
      item_id: itemId,
      name: uploadFile.name,
      path,
      url,
      type: docType,
      size_bytes: uploadFile.size,
      doc_type: docType,
      label,
      file_name: uploadFile.name,
      file_path: path,
      mime_type: uploadFile.type || 'application/octet-stream',
    });

    if (dbErr) setError('Could not record file: ' + dbErr.message);
    await loadDocs();
    setUploading(false);
  }

  async function openPreview(doc) {
    // Always use a fresh signed URL — public URLs don't work on private buckets
    const storagePath = doc.path || doc.file_path;
    if (storagePath) {
      const { data, error: signErr } = await supabase.storage
        .from('item-documents')
        .createSignedUrl(storagePath, 3600);
      if (data?.signedUrl) { setPreviewUrl(data.signedUrl); setPreviewDoc(doc); return; }
      console.warn('openPreview signed URL error:', signErr);
      // If signed URL fails, fall back to stored public URL
      if (doc.url) { setPreviewUrl(doc.url); setPreviewDoc(doc); return; }
      setError('Could not open file. The "item-documents" storage bucket may need to be created in Supabase (Storage → New bucket → item-documents).');
    } else if (doc.url) {
      setPreviewUrl(doc.url);
      setPreviewDoc(doc);
    } else {
      setError('No file path found for this document.');
    }
  }

  async function handleDelete(doc) {
    if (!window.confirm(`Delete "${doc.label || doc.name}"?`)) return;
    await supabase.storage.from('item-documents').remove([doc.path || doc.file_path]);
    await supabase.from('item_documents').delete().eq('id', doc.id);
    setDocs(prev => prev.filter(d => d.id !== doc.id));
  }

  async function handleSaveEdit(doc) {
    await supabase.from('item_documents')
      .update({ label: editLabel, doc_type: editDocType, type: editDocType })
      .eq('id', doc.id);
    setDocs(prev => prev.map(d =>
      d.id === doc.id ? { ...d, label: editLabel, doc_type: editDocType, type: editDocType } : d
    ));
    setEditingId(null);
  }

  const isImageFile = (doc) => (doc.mime_type || '').startsWith('image/');

  if (compact) {
    return <span style={{ fontSize: 10, color: '#888', marginLeft: 4 }}>📎{loading ? '…' : docs.length}</span>;
  }

  const specSheets = docs.filter(d => (d.doc_type || d.type) === 'Spec Sheet');
  const otherDocs  = docs.filter(d => (d.doc_type || d.type) !== 'Spec Sheet');

  return (
    <div>
      {/* Upload controls */}
      {!readOnly && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer?.files[0]; if (f) handleUpload(f); }}
        >
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 4 }}>
            <select value={uploadDocType} onChange={e => setUploadDocType(e.target.value)}
              style={{ fontSize: 11, padding: '4px 8px', border: '1px solid #ddd', borderRadius: 5, background: '#fff', flex: 1 }}>
              {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
            </select>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ padding: '4px 12px', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 5, fontSize: 11, cursor: uploading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}>
              {uploading ? 'Uploading…' : '+ Add File'}
            </button>
            <input ref={fileRef} type="file" style={{ display: 'none' }}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.heic,.txt,.csv"
              onChange={e => { if (e.target.files[0]) handleUpload(e.target.files[0]); e.target.value = ''; }} />
          </div>
          {dragging && (
            <div style={{ border: '2px dashed #1a1a1a', borderRadius: 6, padding: 8, textAlign: 'center', fontSize: 11, color: '#555', marginBottom: 6, background: '#fafafa' }}>
              Drop file here
            </div>
          )}
          <div style={{ fontSize: 10, color: '#bbb', marginBottom: 10 }}>Drag & drop · paste (⌘V)</div>
        </div>
      )}

      {error && (
        <div style={{ fontSize: 11, color: '#c0392b', marginBottom: 6, padding: '5px 8px', background: '#ffebee', borderRadius: 4 }}>{error}</div>
      )}

      {loading && <div style={{ fontSize: 11, color: '#aaa' }}>Loading…</div>}

      {/* ROW 1: Photos */}
      {images.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>Photos</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {images.map((img, i) => (
              <PhotoTile key={i} img={img} index={i} isHero={i === heroIndex} onClick={() => onImageClick && onImageClick(i)} />
            ))}
          </div>
        </div>
      )}

      {/* ROW 2: Spec Sheets */}
      {specSheets.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#1a56db', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>Spec Sheets</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {specSheets.map(doc => (
              <DocTile key={doc.id} doc={doc}
                isSpecSheet={true}
                onOpen={() => openPreview(doc)}
                onEdit={() => { setEditingId(doc.id); setEditLabel(doc.label || doc.name || ''); setEditDocType(doc.doc_type || 'Spec Sheet'); }}
                onDelete={() => handleDelete(doc)}
                readOnly={readOnly}
                isEditing={editingId === doc.id}
                editLabel={editLabel} editDocType={editDocType}
                onEditLabel={setEditLabel} onEditDocType={setEditDocType}
                onSaveEdit={() => handleSaveEdit(doc)} onCancelEdit={() => setEditingId(null)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ROW 3: Other documents */}
      {otherDocs.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#888', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>Documents</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {otherDocs.map(doc => (
              <DocTile key={doc.id} doc={doc}
                isSpecSheet={false}
                onOpen={() => openPreview(doc)}
                onEdit={() => { setEditingId(doc.id); setEditLabel(doc.label || doc.name || ''); setEditDocType(doc.doc_type || 'Other'); }}
                onDelete={() => handleDelete(doc)}
                readOnly={readOnly}
                isEditing={editingId === doc.id}
                editLabel={editLabel} editDocType={editDocType}
                onEditLabel={setEditLabel} onEditDocType={setEditDocType}
                onSaveEdit={() => handleSaveEdit(doc)} onCancelEdit={() => setEditingId(null)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ROW 4: Receipts — derived from the Receipts tab, never uploaded
          here. Each FILE on a linked receipt (vendor invoice, transaction
          printout, …) is its own tile; click opens the same preview modal
          the other documents use. Read-only by design: the receipt lives
          on the Receipts tab. */}
      {linkedReceiptFiles.length > 0 && (
        <div style={{ marginBottom: 8 }} data-testid="item-receipts-group">
          <div style={{ fontSize: 10, fontWeight: 700, color: '#D4007A', letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: 6 }}>
            Receipts <span style={{ color: '#bbb', fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>— linked automatically from the Receipts tab</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
            {linkedReceiptFiles.map((entry, i) => {
              const rc = entry.receipt;
              const roleBit = entry.file.role && entry.file.role !== 'invoice' ? ' · ' + entry.file.role : '';
              const isPdf = (entry.file.mimeType || '').includes('pdf') || (entry.file.filename || '').toLowerCase().endsWith('.pdf');
              return (
                <div key={rc.id + ':' + entry.file.storagePath + ':' + i}
                  title={receiptTileTitle(entry)}
                  onClick={() => openReceiptPreview(entry)}
                  style={{ width: 72, cursor: 'pointer', userSelect: 'none' }}>
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 52, borderRadius: 7, border: '1.5px solid #e0e0e0', background: '#fff', marginBottom: 4 }}>
                    {isPdf ? <PDFIcon type="Receipt" /> : <DocIcon type="Receipt" />}
                  </div>
                  <div style={{ fontSize: 9, color: '#666', textAlign: 'center', lineHeight: 1.3, wordBreak: 'break-word' }}>
                    {(rc.vendorName || 'Receipt').slice(0, 16)}{roleBit}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!loading && docs.length === 0 && images.length === 0 && linkedReceiptFiles.length === 0 && (
        <div style={{ fontSize: 11, color: '#bbb', padding: '10px 0', textAlign: 'center', border: '1px dashed #e0e0e0', borderRadius: 6 }}>
          No files yet
        </div>
      )}

      {/* Preview modal */}
      {previewDoc && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.82)', zIndex: 2000, display: 'flex', flexDirection: 'column' }}
          onClick={e => { if (e.target === e.currentTarget) { setPreviewDoc(null); setPreviewUrl(''); } }}>
          <div style={{ background: '#1a1a1a', padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ color: '#fff', fontSize: 13, fontWeight: 600, flex: 1 }}>
              {previewDoc.label || previewDoc.name || previewDoc.file_name}
            </span>
            <span style={{ color: '#888', fontSize: 11 }}>{previewDoc.doc_type}</span>
            <button onClick={() => window.open(previewUrl, '_blank')}
              style={{ padding: '5px 12px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 5, fontSize: 11, cursor: 'pointer' }}>
              Open full ↗
            </button>
            <button onClick={() => { setPreviewDoc(null); setPreviewUrl(''); }}
              style={{ padding: '5px 10px', background: '#333', color: '#fff', border: '1px solid #555', borderRadius: 5, fontSize: 14, cursor: 'pointer', fontWeight: 700 }}>
              ✕
            </button>
          </div>
          <div style={{ flex: 1, overflow: 'hidden', padding: 8 }}>
            {isImageFile(previewDoc) ? (
              <img src={previewUrl} alt={previewDoc.label}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block', margin: '0 auto' }} />
            ) : (
              <iframe src={previewUrl}
                style={{ width: '100%', height: '100%', border: 'none', borderRadius: 4, background: '#fff' }}
                title={previewDoc.label || previewDoc.file_name} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PhotoTile({ img, index, isHero, onClick }) {
  const [hover, setHover] = useState(false);
  return (
    <div
      onClick={onClick}
      title={img.label || 'Photo'}
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      style={{
        width: 56, height: 56, borderRadius: 6, overflow: 'hidden',
        cursor: 'pointer', flexShrink: 0, position: 'relative',
        border: isHero ? '2px solid #1a1a1a' : hover ? '2px solid #888' : '2px solid #e0e0e0',
        transition: 'border-color 0.12s',
      }}
    >
      <img src={img.url} alt={img.label || ''}
        style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      {isHero && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0, right: 0,
          background: 'rgba(0,0,0,0.6)', fontSize: 7, color: '#fff',
          textAlign: 'center', padding: '1px 0', fontWeight: 700, letterSpacing: '0.5px',
        }}>HERO</div>
      )}
    </div>
  );
}

function DocTile({ doc, isSpecSheet, onOpen, onEdit, onDelete, readOnly, isEditing, editLabel, editDocType, onEditLabel, onEditDocType, onSaveEdit, onCancelEdit }) {
  const [hover, setHover] = useState(false);
  const name = doc.label || doc.name || doc.file_name || 'Untitled';
  const docType = doc.doc_type || doc.type || 'Other';
  const shortName = name.length > 18 ? name.slice(0, 16) + '…' : name;

  if (isEditing) {
    return (
      <div style={{ background: '#f8f8f8', border: '1px solid #ddd', borderRadius: 8, padding: 8, width: 96 }}>
        <input value={editLabel} onChange={e => onEditLabel(e.target.value)}
          style={{ width: '100%', padding: '3px 5px', border: '1px solid #ddd', borderRadius: 4, fontSize: 10, marginBottom: 4, boxSizing: 'border-box' }} />
        <select value={editDocType} onChange={e => onEditDocType(e.target.value)}
          style={{ width: '100%', padding: '3px 5px', border: '1px solid #ddd', borderRadius: 4, fontSize: 10, marginBottom: 6, boxSizing: 'border-box' }}>
          {DOC_TYPES.map(t => <option key={t}>{t}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 3 }}>
          <button onClick={onSaveEdit} style={{ flex: 1, padding: '3px 0', background: '#1a1a1a', color: '#fff', border: 'none', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>✓</button>
          <button onClick={onCancelEdit} style={{ flex: 1, padding: '3px 0', background: '#f0f0f0', border: '1px solid #ddd', borderRadius: 4, fontSize: 10, cursor: 'pointer' }}>✕</button>
        </div>
      </div>
    );
  }

  return (
    <div
      onMouseOver={() => setHover(true)}
      onMouseOut={() => setHover(false)}
      style={{ width: 72, cursor: 'pointer', position: 'relative', userSelect: 'none' }}
    >
      {/* Icon tile */}
      <div
        onClick={onOpen}
        style={{
          display: 'flex', justifyContent: 'center', alignItems: 'center',
          height: 52, borderRadius: 7,
          border: `1.5px solid ${hover ? '#555' : '#e0e0e0'}`,
          background: hover ? '#f5f5f5' : '#fff',
          transition: 'all 0.12s', marginBottom: 4,
        }}
      >
        {isSpecSheet
          ? <SpecSheetIcon />
          : isPDFFile(doc)
            ? <PDFIcon type={docType} />
            : <DocIcon type={docType} />
        }
      </div>

      {/* Direction badge */}
      {doc.direction && (
        <div style={{
          position: 'absolute', top: 3, left: 3,
          fontSize: 7, fontWeight: 700, letterSpacing: '0.3px',
          background: doc.direction === 'outgoing' ? '#e8f0fe' : '#fff3e0',
          color: doc.direction === 'outgoing' ? '#1a56db' : '#e65100',
          borderRadius: 3, padding: '1px 4px', lineHeight: 1.4,
        }}>
          {doc.direction === 'outgoing' ? '→ SENT' : '← VDR'}
        </div>
      )}

      {/* Label */}
      <div style={{ fontSize: 9, color: '#666', textAlign: 'center', lineHeight: 1.3, wordBreak: 'break-word' }}>
        {shortName}
      </div>

      {/* Hover actions */}
      {hover && !readOnly && (
        <div style={{ position: 'absolute', top: -7, right: -7, display: 'flex', gap: 2, zIndex: 10 }}>
          <button onClick={e => { e.stopPropagation(); onEdit(); }}
            style={{ width: 18, height: 18, background: '#fff', border: '1px solid #ccc', borderRadius: 3, fontSize: 9, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✎</button>
          <button onClick={e => { e.stopPropagation(); onDelete(doc); }}
            style={{ width: 18, height: 18, background: '#ffebee', border: '1px solid #ffcdd2', borderRadius: 3, fontSize: 9, cursor: 'pointer', color: '#c62828', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>
      )}
    </div>
  );
}
